import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';

import { CommandBarTypedItem } from '#components/command-bar/components/command-bar-typed-item.js';
import { useTRPC } from '#src/trpc.js';
import { RestartProcessingCommandItem } from './restart-processing-command-item.js';
import { useCommandBarOrganization } from './use-command-bar-organization.js';

import type { CadstromUidResultProps } from '../cadstrom-uid-types.js';

/**
 * Renders the command-bar results for a build id (`b_...`, a revision in the UI):
 * navigation to that revision's analysis results, and — for staff, on a revision whose
 * processing is stuck — a command to restart it.
 *
 * @param uid - The build id to resolve and display.
 * @param onClose - Dismisses the command bar once navigation begins.
 */
export function BuildUidResult({ uid, onClose }: CadstromUidResultProps) {
	const navigate = useNavigate();
	const trpc = useTRPC();

	const { data: revisionData } = useQuery(trpc.revision.get.queryOptions({ id: uid }, { throwOnError: false }));
	const projectId = revisionData?.projectId;

	const { data: projectData } = useQuery(
		trpc.project.getPreview.queryOptions({ id: projectId ?? '' }, { enabled: !!projectId, throwOnError: false }),
	);
	const preview = projectData?.projectPreview;
	const organization = useCommandBarOrganization(preview?.ownerId ?? undefined);

	if (!revisionData || !preview || !organization) {
		return null;
	}

	const revisionName = revisionData.revision.displayName ?? 'Untitled revision';

	return (
		<Command.Group heading='Revisions'>
			<Command.Item
				value={uid}
				keywords={[revisionName, preview.name, 'revision', 'build']}
				onSelect={() => {
					void navigate({
						to: '/orgs/$orgName/projects/$projectId/revisions/$revisionId/results',
						params: {
							orgName: organization.organizationName,
							projectId: preview.id,
							revisionId: revisionData.revision.id,
						},
					});
					onClose();
				}}
			>
				<CommandBarTypedItem type='revision' name={revisionName}>
					<span className='truncate font-normal text-gray-a11'>{preview.name}</span>
				</CommandBarTypedItem>
			</Command.Item>
			<RestartProcessingCommandItem revision={revisionData.revision} revisionName={revisionName} onClose={onClose} />
		</Command.Group>
	);
}
